import { useState } from 'react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { ModalShell } from '../../shared/ModalShell';
import { useRecordVendorInvoice } from '../../../hooks/usePurchaseOrders';
import { useToast } from '../../../providers/ToastProvider';
import { uiErrorMessage } from '../../../utils/apiError';
import { uploadVendorInvoiceFile } from '../../../services/purchaseOrderFiles';
import type { PurchaseOrderDetail } from '../../../services/purchaseOrders';
import { ExpectedCost } from './ExpectedCost';
import { ModalField, MODAL_INPUT_CLASSES } from '../../shared/ModalField';
import { VendorInvoiceFileField } from './VendorInvoiceFileField';
import { expectedCostSummary } from './poCost';

interface UpdateBillingModalProps {
    purchaseOrder: PurchaseOrderDetail;
    onClose: () => void;
}

export function UpdateBillingModal({ purchaseOrder, onClose }: UpdateBillingModalProps) {
    const { showToast } = useToast();
    const { mutateAsync: recordInvoice, isPending } = useRecordVendorInvoice();

    const [invoiceNumber, setInvoiceNumber] = useState(purchaseOrder.vendorInvoiceNumber ?? '');
    const [amount, setAmount] = useState(purchaseOrder.vendorInvoicedAmount ?? '');
    const [dueDate, setDueDate] = useState(purchaseOrder.vendorInvoiceDueDate?.slice(0, 10) ?? '');
    // Frozen at mount like every sibling field: a refetch landing mid-edit must
    // not turn an untouched empty input into an explicit clear.
    const [initialPaidAt] = useState(purchaseOrder.paidAt?.slice(0, 10) ?? '');
    const [paidAt, setPaidAt] = useState(initialPaidAt);
    const [file, setFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    const parsedAmount = Number(amount);
    const amountValid =
        amount.trim().length > 0 && Number.isFinite(parsedAmount) && parsedAmount >= 0;
    const busy = isPending || isUploading;
    const canSubmit = invoiceNumber.trim().length > 0 && amountValid && !busy;

    const handleSubmit = async () => {
        try {
            // Uploaded first: the id has to exist before the record call, and a
            // failed upload must not silently record an invoice with no document.
            let vendorInvoiceFileId = purchaseOrder.vendorInvoiceFileId ?? undefined;
            if (file) {
                setIsUploading(true);
                vendorInvoiceFileId = (await uploadVendorInvoiceFile(purchaseOrder.id, file))
                    .fileId;
                setIsUploading(false);
            }

            await recordInvoice({
                id: purchaseOrder.id,
                body: {
                    vendorInvoiceNumber: invoiceNumber.trim(),
                    vendorInvoicedAmount: parsedAmount,
                    vendorInvoiceDueDate: dueDate || undefined,
                    vendorInvoiceFileId,
                    // Unchanged from mount = omit; only send when the operator actually edited it.
                    paidAt: paidAt === initialPaidAt ? undefined : paidAt || null
                }
            });
            showToast('Vendor invoice recorded', 'success');
            onClose();
        } catch (error) {
            setIsUploading(false);
            showToast(uiErrorMessage(error, 'Failed to record the vendor invoice'), 'error');
        }
    };

    const summary = expectedCostSummary(purchaseOrder);

    return (
        <ModalShell
            widthClassName="max-w-[460px]"
            panelClassName="rounded-xl p-6"
            dismissible={!busy}
            onDismiss={onClose}
            labelledBy="po-invoice-title"
        >
            <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                    <DocumentTextIcon className="h-5 w-5 text-gray-600" />
                </span>
                <div className="min-w-0">
                    <h2 id="po-invoice-title" className="text-lg font-bold text-gray-800">
                        Update billing
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                        {purchaseOrder.vendor.name} · {purchaseOrder.poNumber}
                    </p>
                </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between gap-4 mt-4">
                <span className="text-sm text-gray-500">Expected cost</span>
                <ExpectedCost summary={summary} />
            </div>

            <div className="mt-4 flex flex-col gap-4">
                <ModalField htmlFor="po-invoice-number" label="Invoice number" required>
                    <input
                        id="po-invoice-number"
                        type="text"
                        value={invoiceNumber}
                        onChange={(e) => setInvoiceNumber(e.target.value)}
                        autoFocus
                        className={MODAL_INPUT_CLASSES}
                    />
                </ModalField>

                <ModalField
                    htmlFor="po-invoice-amount"
                    label="Amount invoiced (USD)"
                    required
                    hintTone="error"
                    hint={
                        amount.trim().length > 0 && !amountValid
                            ? 'Enter a valid amount.'
                            : undefined
                    }
                >
                    <input
                        id="po-invoice-amount"
                        type="number"
                        min={0}
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        className={MODAL_INPUT_CLASSES}
                    />
                </ModalField>

                <ModalField htmlFor="po-invoice-due" label="Due date">
                    <input
                        id="po-invoice-due"
                        type="date"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        className={MODAL_INPUT_CLASSES}
                    />
                </ModalField>

                <ModalField htmlFor="po-invoice-paid" label="Paid">
                    <input
                        id="po-invoice-paid"
                        type="date"
                        value={paidAt}
                        onChange={(e) => setPaidAt(e.target.value)}
                        className={MODAL_INPUT_CLASSES}
                    />
                </ModalField>

                <VendorInvoiceFileField
                    file={file}
                    hasStoredFile={Boolean(purchaseOrder.vendorInvoiceFileId)}
                    onPick={setFile}
                    onReject={(reason) => showToast(reason, 'error')}
                />
            </div>

            <div className="flex justify-end items-center gap-3 mt-6">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={busy}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={() => {
                        void handleSubmit();
                    }}
                    disabled={!canSubmit}
                    className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isUploading ? 'Uploading…' : isPending ? 'Saving…' : 'Save invoice'}
                </button>
            </div>
        </ModalShell>
    );
}
